import React from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { grid } from '../../../Global/global_variables.js';
import { Frame } from '../../../Simulations/Frame.js';

// Selection state for items (layers and frames)
interface LayerListProps {
    selectedItems: Set<string>;
    setSelectedItems: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const LayerList: React.FC<LayerListProps> = ({ selectedItems, setSelectedItems }) => {
    // Accepts a string or array of strings, toggles all together
    const toggleSelection = (ids: string | string[]) => {
        setSelectedItems((prev) => {
            const newSet = new Set(prev);
            const idList = Array.isArray(ids) ? ids : [ids];
            const allSelected = idList.every((id) => newSet.has(id));
            idList.forEach((id) => {
                if (allSelected) {
                    newSet.delete(id);
                } else {
                    newSet.add(id);
                }
            });
            return newSet;
        });
    };

    const layerNodes: React.ReactNode[] = grid.geodatas.map((geodata, gIndex) => {
        const geodataId = `geodata-${gIndex}`;
        const geodataLabel = geodata.name || `Geodata ${gIndex + 1}`;

        const simulationNodes = (geodata.simulationSets || []).map((simulationSet, sIndex) => {
            const simulationId = `${geodataId}-sim-${sIndex}`;
            const simulationLabel = simulationSet.name || `Simulation ${sIndex + 1}`;

            const layerItems = (simulationSet.layerCollection || []).map((layer, lIndex) => {
                const name = layer.name || '(no name)';
                const id = `layer-${gIndex}-${sIndex}-${lIndex}`;
                const frameCollection = layer.getFrameCollection() || [];

                if (frameCollection.length <= 1) {
                    return (
                        <TreeItem
                            itemId={id}
                            key={id}
                            label={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedItems.has(id)}
                                        onChange={() => toggleSelection(id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSelection(id);
                                        }}
                                    >
                                        {name}
                                    </span>
                                </div>
                            }
                        />
                    );
                }

                return (
                    <TreeItem
                        itemId={id}
                        key={id}
                        label={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                    type="checkbox"
                                    checked={
                                        selectedItems.has(id) && frameCollection.every((_, i) => selectedItems.has(`${id}-frame-${i}`))
                                    }
                                    onChange={() => toggleSelection([id, ...frameCollection.map((_, i) => `${id}-frame-${i}`)])}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <span style={{ cursor: 'pointer' }}>{name}</span>
                            </div>
                        }
                    >
                        {frameCollection.map((frame, fIndex) => {
                            const frameId = `${id}-frame-${fIndex}`;
                            return (
                                <TreeItem
                                    itemId={frameId}
                                    key={frameId}
                                    label={
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.has(frameId)}
                                                    onChange={() => toggleSelection(frameId)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </span>
                                            <span style={{ cursor: 'pointer' }}>{(frame as Frame).getName?.() || `(no frame name)`}</span>
                                        </div>
                                    }
                                />
                            );
                        })}
                    </TreeItem>
                );
            });

            return (
                <TreeItem itemId={simulationId} key={simulationId} label={simulationLabel}>
                    {layerItems}
                </TreeItem>
            );
        });

        return (
            <TreeItem itemId={geodataId} key={geodataId} label={geodataLabel}>
                {simulationNodes}
            </TreeItem>
        );
    });

    return (
        <div style={{ padding: '4px', backgroundColor: 'transparent', maxHeight: '240px', overflowY: 'auto' }}>
            <SimpleTreeView>{layerNodes}</SimpleTreeView>
        </div>
    );
};

export default LayerList;
